import {
	faFilter,
	faMagnifyingGlass,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PortalClientDto } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useEffect, useState } from 'react';
import { AccountLocationPicker } from './AccountLocationPicker';

/**
 * Filtering for the history surfaces — Orders, Invoices, Statements,
 * Transactions (PD-14674, reworked in PD-14701).
 *
 * The search box (when a surface has one) stays on the page; every other
 * filter — relative date range, document type, payment status, location —
 * folds into ONE tray that opens from a filter button styled to match the
 * search box, and commits with explicit Apply / Cancel. The tray holds its
 * own DRAFT copy of the values, so nothing changes the list until Apply;
 * Cancel discards the draft.
 */

/** How many previous whole years to offer beneath the current one. */
const PREVIOUS_YEARS = 4;

export const DEFAULT_HISTORY_RANGE = '3m';

export interface HistoryRangeBounds {
	/** Inclusive ISO date lower bound, when the range has one. */
	dateFrom?: string;
	/** Inclusive ISO date upper bound, when the range has one. */
	dateTo?: string;
}

function isoDate(d: Date): string {
	const y = d.getFullYear();
	const m = `${d.getMonth() + 1}`.padStart(2, '0');
	const day = `${d.getDate()}`.padStart(2, '0');
	return `${y}-${m}-${day}`;
}

/** The selectable range keys, in display order. */
export function historyRangeOptions(): Array<{ key: string; label: string }> {
	const now = new Date();
	const years: Array<{ key: string; label: string }> = [];
	for (
		let y = now.getFullYear();
		y >= now.getFullYear() - PREVIOUS_YEARS;
		y--
	) {
		years.push({ key: `${y}`, label: `${y}` });
	}
	return [
		{ key: '30d', label: 'Last 30 days' },
		{ key: '3m', label: 'Last 3 months' },
		...years,
		// Explicit full-history escape hatch (PD-14699) — also the target of
		// the filtered-empty state's "View all time" action.
		{ key: 'all', label: 'All time' },
	];
}

/**
 * Resolve a range key to concrete inclusive ISO date bounds for the API.
 * 'all' means no bounds (an explicit user choice); unknown keys fall back
 * to the default (last 3 months) so a hand-edited URL can't silently show
 * the unfiltered full history.
 */
export function resolveHistoryRange(range: string): HistoryRangeBounds {
	const now = new Date();
	if (range === 'all') {
		return {};
	}
	if (/^\d{4}$/.test(range)) {
		const year = Number(range);
		// dateTo = Jan 1 of the next year: with the API's inclusive `<=`
		// comparison against datetime columns this covers every moment of
		// Dec 31, which a `YYYY-12-31` bound (midnight) would not.
		return { dateFrom: `${year}-01-01`, dateTo: `${year + 1}-01-01` };
	}
	if (range === '30d') {
		const from = new Date(now);
		from.setDate(from.getDate() - 30);
		return { dateFrom: isoDate(from) };
	}
	// '3m' and anything unrecognized → last 3 months.
	const from = new Date(now);
	from.setMonth(from.getMonth() - 3);
	return { dateFrom: isoDate(from) };
}

export function historyRangeLabel(range: string): string {
	const match = historyRangeOptions().find((o) => o.key === range);
	return match?.label ?? 'Last 3 months';
}

// --- Filter field configuration ---

/** A dropdown filter (document type, payment status). */
interface SelectFilterField {
	kind: 'select';
	/** URL/param key this field maps to. */
	key: string;
	label: string;
	/** Options; include an empty-value option for "All". */
	options: Array<{ value: string; label: string }>;
}

/** The relative date-range presets (shared across every surface). */
interface RangeFilterField {
	kind: 'range';
	/** Always 'range'. */
	key: 'range';
	label: string;
	/**
	 * The range key an empty value stands for — shown in the select and
	 * treated as "not an active filter". History surfaces keep the '3m'
	 * default; the Make-a-Payment open-invoice list uses 'all' (hiding an
	 * old unpaid invoice by default would hide exactly what most needs
	 * paying).
	 */
	defaultRange?: string;
}

/** The account/location picker — only when the visitor has multiple. */
interface LocationFilterField {
	kind: 'location';
	key: 'clientId';
	label: string;
	clients: Array<PortalClientDto>;
}

/** A yes/no checkbox filter (e.g. "Overdue only"). '1' = on, '' = off. */
interface ToggleFilterField {
	kind: 'toggle';
	key: string;
	label: string;
}

export type HistoryFilterField =
	| SelectFilterField
	| RangeFilterField
	| LocationFilterField
	| ToggleFilterField;

/** Is a committed value for a field an active (non-default) filter? */
function isFieldActive(field: HistoryFilterField, value: string): boolean {
	if (field.kind === 'range') {
		// '' is the field's default range; anything else is an explicit pick.
		return (
			value !== '' && value !== (field.defaultRange ?? DEFAULT_HISTORY_RANGE)
		);
	}
	return value !== '';
}

function countActiveFilters(
	fields: Array<HistoryFilterField>,
	values: Record<string, string>,
): number {
	return fields.reduce(
		(n, f) => n + (isFieldActive(f, values[f.key] ?? '') ? 1 : 0),
		0,
	);
}

interface HistoryFiltersProps {
	/** Filters for this surface, rendered top-to-bottom in the tray. */
	fields: Array<HistoryFilterField>;
	/** Committed values, keyed by field key ('range', 'type', ...). */
	values: Record<string, string>;
	/** Commit the tray's draft — the page maps these to its URL params. */
	onApply: (next: Record<string, string>) => void;
	/** Omit the search props to render the filter control alone. */
	searchValue?: string;
	onSearchChange?: (value: string) => void;
	onSearchSubmit?: () => void;
	searchPlaceholder?: string;
}

export function HistoryFilters({
	fields,
	values,
	onApply,
	searchValue,
	onSearchChange,
	onSearchSubmit,
	searchPlaceholder,
}: HistoryFiltersProps): JSX.Element {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	// The tray's working copy — seeded from committed values each time it
	// opens, so Cancel (or dismissing) reliably discards edits.
	const [draft, setDraft] = useState<Record<string, string>>(values);

	useEffect(() => {
		if (isOpen) {
			setDraft(values);
		}
	}, [isOpen, values]);

	// Lock body scroll while the tray is open, and close on Escape.
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const onKey = (e: KeyboardEvent): void => {
			if (e.key === 'Escape') {
				setIsOpen(false);
			}
		};
		document.addEventListener('keydown', onKey);
		return (): void => {
			document.removeEventListener('keydown', onKey);
		};
	}, [isOpen]);

	const hasSearch = onSearchChange != null && onSearchSubmit != null;
	const activeCount = countActiveFilters(fields, values);

	const setDraftValue = (key: string, value: string): void => {
		setDraft((prev) => ({ ...prev, [key]: value }));
	};

	const handleApply = (): void => {
		onApply(draft);
		setIsOpen(false);
	};

	const handleClearAll = (): void => {
		const cleared: Record<string, string> = {};
		for (const f of fields) {
			cleared[f.key] = '';
		}
		setDraft(cleared);
	};

	return (
		<>
			<div className="flex items-center gap-2">
				{hasSearch && (
					<form
						className="relative flex-1 min-w-0"
						onSubmit={(e) => {
							e.preventDefault();
							onSearchSubmit();
						}}
					>
						<FontAwesomeIcon
							icon={faMagnifyingGlass}
							className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-200 text-sm pointer-events-none"
						/>
						<input
							type="search"
							value={searchValue ?? ''}
							onChange={(e) => {
								onSearchChange(e.target.value);
							}}
							placeholder={searchPlaceholder ?? t('Search...')}
							className="search-on-surface w-full pl-9 pr-3 py-2.5 rounded-lg bg-brand-surface border border-brand-border text-sm text-gray-900 dark:text-gray-100"
						/>
					</form>
				)}
				{/* Filter button — matches the search box (py-2.5, rounded-lg,
				    same surface/border) so the row reads as one control set. */}
				<button
					type="button"
					onClick={() => {
						setIsOpen(true);
					}}
					className={`${hasSearch ? 'shrink-0' : 'w-full justify-center'} flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-3 py-2.5 text-sm font-medium text-gray-900 dark:text-gray-100`}
					aria-haspopup="dialog"
					aria-expanded={isOpen}
				>
					<FontAwesomeIcon icon={faFilter} className="h-3.5 w-3.5" />
					<span>{t('Filters')}</span>
					{activeCount > 0 && (
						<span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-portal-accent text-white text-[11px] font-semibold leading-none">
							{activeCount}
						</span>
					)}
				</button>
			</div>

			{isOpen && (
				<>
					<button
						type="button"
						aria-label={t('Cancel')}
						onClick={() => {
							setIsOpen(false);
						}}
						className="fixed inset-0 z-40 bg-black/40 drawer-backdrop-in"
					/>
					<div
						className="fixed inset-x-0 z-40 rounded-t-2xl bg-brand-surface shadow-2xl flex flex-col overflow-hidden drawer-slide-up max-h-[80vh]"
						// Matches BottomNav's real footprint: 48px tab row + (inset
						// − 12px overlap), floored at 48px for inset-less browsers.
						style={{
							bottom: 'max(calc(36px + env(safe-area-inset-bottom)), 48px)',
						}}
						role="dialog"
						aria-modal="true"
						aria-label={t('Filters')}
					>
						<div className="flex items-center justify-between px-4 h-12 border-b border-brand-border shrink-0">
							<span className="text-base font-semibold text-gray-900 dark:text-gray-100">
								{t('Filters')}
							</span>
							<button
								type="button"
								onClick={handleClearAll}
								className="text-sm text-portal-accent hover:underline"
							>
								{t('Clear all')}
							</button>
						</div>

						<div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
							{fields.map((field) => (
								<div key={field.key}>
									{/* Toggle rows carry their own inline label. */}
									{field.kind !== 'toggle' && (
										<label className="block text-xs font-medium text-brand-muted mb-1">
											{t(field.label)}
										</label>
									)}
									{field.kind === 'range' ? (
										<select
											value={
												draft.range === ''
													? (field.defaultRange ?? DEFAULT_HISTORY_RANGE)
													: draft.range
											}
											onChange={(e) => {
												setDraftValue('range', e.target.value);
											}}
											className="w-full px-3 py-2.5 rounded-lg bg-brand-input border border-brand-border text-sm"
										>
											{historyRangeOptions().map((opt) => (
												<option key={opt.key} value={opt.key}>
													{t(opt.label)}
												</option>
											))}
										</select>
									) : field.kind === 'toggle' ? (
										<label className="flex items-center gap-2.5 cursor-pointer min-h-touch">
											<input
												type="checkbox"
												checked={draft[field.key] === '1'}
												onChange={(e) => {
													setDraftValue(field.key, e.target.checked ? '1' : '');
												}}
												className="w-4 h-4 text-portal-accent"
											/>
											<span className="text-sm text-gray-900 dark:text-gray-100">
												{t(field.label)}
											</span>
										</label>
									) : field.kind === 'location' ? (
										<AccountLocationPicker
											value={draft.clientId}
											onChange={(v) => {
												setDraftValue('clientId', v);
											}}
											clients={field.clients}
											className="w-full"
										/>
									) : (
										<select
											value={draft[field.key]}
											onChange={(e) => {
												setDraftValue(field.key, e.target.value);
											}}
											className="w-full px-3 py-2.5 rounded-lg bg-brand-input border border-brand-border text-sm"
										>
											{field.options.map((opt) => (
												<option key={opt.value} value={opt.value}>
													{t(opt.label)}
												</option>
											))}
										</select>
									)}
								</div>
							))}
						</div>

						<div className="flex items-center gap-3 px-4 py-3 border-t border-brand-border shrink-0">
							<button
								type="button"
								onClick={() => {
									setIsOpen(false);
								}}
								className="flex-1 px-4 py-2.5 rounded-lg border border-brand-border bg-brand-surface text-sm font-medium text-gray-900 dark:text-gray-100 min-h-touch"
							>
								{t('Cancel')}
							</button>
							<button
								type="button"
								onClick={handleApply}
								className="flex-1 px-4 py-2.5 rounded-lg bg-portal-accent hover:bg-portal-accent-hover text-white text-sm font-semibold min-h-touch"
							>
								{t('Apply')}
							</button>
						</div>
					</div>
				</>
			)}
		</>
	);
}
